import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getMatches2025, getLeagues } from '../services/highlightlyApi';
import { BRAND_GREEN, BRAND_GOLD } from '../theme/sucronTheme';
import { useSportsLang } from '../contexts/SportsLangContext';

const LABELS = {
  pt: {
    pageTitle: 'Partidas 2025',
    pageSubtitle: 'Explore as partidas de futebol de 2025 com dados reais da API Highlightly Sports',
    filterByLeague: 'Filtrar por Liga',
    allLeagues: 'Todas as Ligas',
    totalMatches: (n) => `Total: ${n} partidas`,
    live: 'AO VIVO',
    errorTitle: 'Ocorreu um erro',
    noMatches: 'Nenhuma partida encontrada para os filtros selecionados.',
    prev: 'Anterior',
    next: 'Próxima',
    dateLocale: 'pt-BR',
  },
  en: {
    pageTitle: 'Matches 2025',
    pageSubtitle: 'Explore 2025 football matches with real data from the Highlightly Sports API',
    filterByLeague: 'Filter by League',
    allLeagues: 'All Leagues',
    totalMatches: (n) => `Total: ${n} matches`,
    live: 'LIVE',
    errorTitle: 'An error occurred',
    noMatches: 'No matches found for the selected filters.',
    prev: 'Previous',
    next: 'Next',
    dateLocale: 'en-US',
  }
};


// --- Cores da marca ---

// --- UI Components ---
const LoadingSpinner = () => (
  <div className="flex justify-center items-center py-10 w-full">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: BRAND_GREEN }}></div>
  </div>
);

const ErrorMessage = ({ message }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  return (
  <div className="text-center py-6 text-red-700 bg-red-100 p-4 rounded-lg border border-red-200">
    <p className="font-bold">{t.errorTitle}</p>
    <p className="text-sm">{message}</p>
  </div>
  );
};

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-xl border border-gray-200 shadow-sm p-4 lg:p-5 ${className}`}>
    {children}
  </div>
);

// --- Componente de Partida ---
const MatchCard = ({ match }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  const isLive = match.status?.ongoing || false;
  const matchDate = new Date(match.dateTime || match.date);
  
  return (
    <Link to={`/futebol/partida/${match.id}`}>
      <Card className="hover:shadow-md transition-all duration-200 cursor-pointer">
        <div className="flex items-center justify-between gap-4">
          {/* Informações da Liga e Data */}
          <div className="flex-shrink-0">
            <div className="text-xs font-semibold mb-1" style={{ color: BRAND_GREEN }}>
              {match.league?.name || 'Liga'}
            </div>
            <div className="text-xs text-gray-600">
              {matchDate.toLocaleDateString(t.dateLocale)}
            </div>
            <div className="text-xs text-gray-600">
              {matchDate.toLocaleTimeString(t.dateLocale, { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>

          {/* Times */}
          <div className="flex-1 flex items-center justify-between gap-4">
            {/* Time Casa */}
            <div className="flex items-center gap-3 flex-1">
              <img 
                src={match.homeTeam?.logo} 
                alt={match.homeTeam?.name} 
                className="w-10 h-10 object-contain"
                onError={(e) => { e.target.src = 'https://via.placeholder.com/40?text=?'; }}
              />
              <div className="flex-1">
                <div className="font-semibold text-gray-900">{match.homeTeam?.name}</div>
              </div>
              <div className="text-2xl font-bold" style={{ color: BRAND_GREEN }}>
                {match.homeTeam?.score ?? '-'}
              </div>
            </div>

            {/* Separador */}
            <div className="text-gray-400 font-bold">×</div>

            {/* Time Visitante */}
            <div className="flex items-center gap-3 flex-1">
              <div className="text-2xl font-bold" style={{ color: BRAND_GREEN }}>
                {match.awayTeam?.score ?? '-'}
              </div>
              <div className="flex-1 text-right">
                <div className="font-semibold text-gray-900">{match.awayTeam?.name}</div>
              </div>
              <img 
                src={match.awayTeam?.logo} 
                alt={match.awayTeam?.name} 
                className="w-10 h-10 object-contain"
                onError={(e) => { e.target.src = 'https://via.placeholder.com/40?text=?'; }}
              />
            </div>
          </div>

          {/* Status */}
          <div className="flex-shrink-0 text-center">
            {isLive ? (
              <span className="inline-block text-xs font-bold px-3 py-1 rounded-full text-white" style={{ backgroundColor: BRAND_GOLD }}>
                {t.live}
              </span>
            ) : (
              <span className="text-xs text-gray-600">
                {match.status?.name || match.status}
              </span>
            )}
          </div>
        </div>
      </Card>
    </Link>
  );
};

// --- Página Principal ---
const MatchesListPage = () => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  const [matches, setMatches] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const matchesPerPage = 20;

  // Carregar ligas disponíveis
  useEffect(() => {
    const loadLeagues = async () => {
      try {
        const response = await getLeagues({ limit: 100 });
        console.log('[MatchesListPage] Ligas carregadas:', response.data);
        setLeagues(response.data.items || []);
      } catch (err) {
        console.warn('[MatchesListPage] Erro ao carregar ligas:', err.message);
      }
    };

    loadLeagues();
  }, []);

  // Carregar partidas de 2025
  useEffect(() => {
    const loadMatches = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('[MatchesListPage] Carregando partidas de 2025...');

        const params = {
          limit: 100,
          offset: 0
        };

        if (selectedLeague) {
          params.leagueId = selectedLeague;
        }

        const response = await getMatches2025(params);
        console.log('[MatchesListPage] Partidas recebidas:', response.data);

        const matchesData = response.data.items || [];
        
        // Ordenar por data (mais recentes primeiro)
        const sortedMatches = matchesData.sort((a, b) => {
          const dateA = new Date(a.dateTime || a.date);
          const dateB = new Date(b.dateTime || b.date);
          return dateB - dateA;
        });

        setMatches(sortedMatches);
        setLoading(false);
      } catch (err) {
        console.error('[MatchesListPage] Erro ao carregar partidas:', err);
        setError(err.response?.data?.message || err.message || 'Erro ao carregar partidas');
        setLoading(false);
      }
    };

    loadMatches();
  }, [selectedLeague]);

  // Paginação
  const indexOfLastMatch = currentPage * matchesPerPage;
  const indexOfFirstMatch = indexOfLastMatch - matchesPerPage;
  const currentMatches = matches.slice(indexOfFirstMatch, indexOfLastMatch);
  const totalPages = Math.ceil(matches.length / matchesPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <main className="container mx-auto px-4 lg:px-8 py-10" style={{ backgroundColor: '#f9fafb' }}>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl lg:text-4xl font-extrabold mb-4" style={{ color: BRAND_GREEN }}>
          {t.pageTitle}
        </h1>
        <p className="text-gray-600">
          {t.pageSubtitle}
        </p>
      </div>

      {/* Filtros */}
      <div className="mb-6">
        <Card>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1">
              <label className="block text-sm font-semibold mb-2" style={{ color: BRAND_GREEN }}>
                {t.filterByLeague}
              </label>
              <select
                value={selectedLeague}
                onChange={(e) => {
                  setSelectedLeague(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="">{t.allLeagues}</option>
                {leagues.map(league => (
                  <option key={league.id} value={league.id}>
                    {league.name} - {league.country?.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="text-sm text-gray-600">
              {t.totalMatches(matches.length)}
            </div>
          </div>
        </Card>
      </div>

      {/* Loading */}
      {loading && <LoadingSpinner />}

      {/* Error */}
      {error && <ErrorMessage message={error} />}

      {/* Lista de Partidas */}
      {!loading && !error && (
        <>
          {currentMatches.length > 0 ? (
            <div className="space-y-4">
              {currentMatches.map(match => (
                <MatchCard key={match.id} match={match} />
              ))}
            </div>
          ) : (
            <Card>
              <p className="text-center text-gray-600 py-8">
                  {t.noMatches}
              </p>
            </Card>
          )}

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="mt-8 flex justify-center">
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  style={{ 
                    backgroundColor: currentPage === 1 ? '#e5e7eb' : BRAND_GREEN,
                    color: currentPage === 1 ? '#9ca3af' : 'white'
                  }}
                >
                  {t.prev}
                </button>

                <div className="flex gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className="w-10 h-10 rounded-lg font-medium transition-all duration-200"
                        style={{ 
                          backgroundColor: currentPage === pageNum ? BRAND_GREEN : 'white',
                          color: currentPage === pageNum ? 'white' : BRAND_GREEN,
                          border: `2px solid ${BRAND_GREEN}`
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  style={{ 
                    backgroundColor: currentPage === totalPages ? '#e5e7eb' : BRAND_GREEN,
                    color: currentPage === totalPages ? '#9ca3af' : 'white'
                  }}
                >
                  {t.next}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </main>
  );
};

export default MatchesListPage;
